import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Upload, ArrowLeft, Save, RefreshCw, FolderOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';
import axios from 'axios';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSettings = () => {
  const [settings, setSettings] = useState({
    home_hero_image: '',
    about_hero_image: '',
    about_section_image: ''
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(null); // stores the setting key being edited
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, settingKey) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await adminFetch('/admin/upload/cover', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      setSettings(prev => ({ ...prev, [settingKey]: data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminFetch('/admin/site-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Settings saved! Refresh your store to see changes.');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const imageSettings = [
    {
      key: 'home_hero_image',
      title: 'Home Page Hero Image',
      description: 'Main banner image on your home page (1920x800px recommended)',
      preview: settings.home_hero_image
    },
    {
      key: 'about_hero_image',
      title: 'About Page Hero Image',
      description: 'Background image for your About page header (1920x800px recommended)',
      preview: settings.about_hero_image
    },
    {
      key: 'about_section_image',
      title: 'About Section Image',
      description: 'Image next to Mestre Curtis Pierre story (800x800px recommended)',
      preview: settings.about_section_image
    }
  ];

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold font-display text-foreground">
                Site Images
              </h1>
              <p className="text-muted-foreground mt-1">
                Upload and manage images across your store
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold shadow-glow transition-all hover:scale-105 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Image Settings */}
        <div className="space-y-6">
          {imageSettings.map((setting, index) => (
            <motion.div
              key={setting.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Info & Upload */}
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-1">
                        {setting.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {setting.description}
                      </p>
                    </div>
                  </div>

                  {/* Upload Button */}
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, setting.key)}
                      disabled={uploading}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 px-6 py-4 bg-background border-2 border-dashed border-border hover:border-primary/50 rounded-xl cursor-pointer transition-all group">
                      <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      <span className="text-foreground font-semibold">
                        {uploading ? 'Uploading...' : 'Upload New Image'}
                      </span>
                    </div>
                  </label>

                  {/* Current URL */}
                  <div className="mt-4">
                    <label className="block text-xs font-bold text-muted-foreground mb-2">
                      Current Image URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings[setting.key]}
                        onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: e.target.value }))}
                        className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="https://..."
                      />
                      <button
                        type="button"
                        onClick={() => setShowPicker(setting.key)}
                        className="px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <FolderOpen className="w-4 h-4" />
                        <span className="text-sm">Browse</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Preview */}
                <div>
                  <p className="text-sm font-bold text-muted-foreground mb-3">Preview</p>
                  {setting.preview ? (
                    <div className="relative aspect-video rounded-xl overflow-hidden border border-border/50">
                      <img
                        src={setting.preview}
                        alt={setting.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23292524" width="400" height="300"/%3E%3Ctext fill="%23A8A29E" font-family="sans-serif" font-size="20" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video rounded-xl bg-background border-2 border-dashed border-border flex items-center justify-center">
                      <div className="text-center">
                        <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-sm text-muted-foreground">No image uploaded</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-primary/10 border border-primary/20 rounded-2xl p-6"
        >
          <div className="flex items-start gap-3">
            <RefreshCw className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-foreground mb-2">How it works</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Upload images using the buttons above</li>
                <li>• Or click Browse to select from Media Library</li>
                <li>• Click "Save Changes" when done</li>
                <li>• Refresh your store to see the new images!</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Media Library Picker */}
      <MediaLibraryPicker
        isOpen={!!showPicker}
        onClose={() => setShowPicker(null)}
        onSelect={(img) => {
          if (showPicker) {
            setSettings(prev => ({ ...prev, [showPicker]: img.url }));
          }
          setShowPicker(null);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};
